import {
	current,
	createSlice,
	createAsyncThunk,
	createEntityAdapter
} from '@reduxjs/toolkit'
import {
	envAwarePost,
	envAwareFormSubmit,
	slidingQueryComparision
} from '../util/util'
import {
	selectNotesIds,
	selectAllNotes,
	getAllNotes,
	deleteNotes
} from './clientNotesSlice'

function splitOnce(s, on) {
	const [first, ...rest] = s.split(on)
	return [first, rest.length > 0? rest.join(on) : null]
}

//helper function for finding an exercise inside of a routine
export const extractExerciseFromRoutine = (routine, type, id) => {
	if(!routine) return null
	for (let i = routine.length - 1; i >= 0; i--) {
		if(routine[i].type==='heading') continue
		const group = routine[i].exercises
		for (let j = group.length - 1; j >= 0; j--) {
			const exercise = group[j]
			if (
				exercise.type === type &&
				exercise.exercise_id === id
			) {
				return exercise
			}
		}
	}
	return null
}

export const extractExerciseLocationFromRoutine = (routine, type, id) => {
	if(!routine) return {groupIdx:null,exerciseIdx:null,exercise:null}
	for (let i = routine.length - 1; i >= 0; i--) {
		if(routine[i].type==='heading') continue
		const group = routine[i].exercises
		for (let j = group.length - 1; j >= 0; j--) {
			const exercise = group[j]
			if (
				exercise.type === type &&
				exercise.exercise_id === id
			) {
				return {groupIdx:i,exerciseIdx:j,exercise}
			}
		}
	}
	return {groupIdx:null,exerciseIdx:null,exercise:null}
}

export const updateTrackedExerciseItems = createAsyncThunk(
	'updateTrackedExerciseItems',
	async payload => await envAwarePost(
		'api/events/',{action:'updateTrackedExerciseItems',...payload}
	)
)

export const logNutritionMealPhoto = createAsyncThunk(
	'logNutritionMealPhoto',
	async payload => await envAwarePost(
		'api/events/',{action:'logNutritionMealPhoto',include_nutrition_meal_photos:1,...payload}
	)
)

export const updateNutritionMealPhoto = createAsyncThunk(
	'updateNutritionMealPhoto',
	async payload => await envAwarePost(
		'api/events/',{action:'updateNutritionMealPhoto',include_nutrition_meal_photos:1,...payload}
	)
)

export const removeNutritionMealPhoto = createAsyncThunk(
	'removeNutritionMealPhoto',
	async payload => await envAwarePost(
		'api/events/',{action:'removeNutritionMealPhoto',include_nutrition_meal_photos:1,...payload}
	)
)

export const fillExerciseWithLastAssignedData = createAsyncThunk(
	'events/fillExerciseWithLastAssignedData',
	async payload => await envAwarePost(
		'api/events/',
		{action:'fillExerciseWithLastAssignedData',...payload}
	)
)

export const copySuperset = createAsyncThunk(
	'events/copySuperset',
	async payload => await envAwarePost(
		'api/events/',
		{action:'copySuperset',...payload}
	)
)

export const updateNutritionOrder = createAsyncThunk(
	'updateNutritionOrder',
	async payload => await envAwarePost (
		'api/events/',
		{action:'updateNutritionOrder',...payload}
	)
)

export const swapNutritionFood = createAsyncThunk(
	'swapNutritionFood',
	async payload => await envAwarePost(
		'api/events/',
		{action:'swapNutritionFood',...payload}
	)
)

export const removeFoodFromMeal = createAsyncThunk(
	'removeFoodFromMeal',
	async payload => envAwarePost(
		'api/events/',
		{action:'removeFoodFromMeal',...payload}
	)
)

export const addFoodToMeal = createAsyncThunk(
	'addFoodToMeal',
	async payload => envAwarePost(
		'api/events/',
		{action:'addFoodToMeal',...payload}
	)
)

export const updateMealOrder = createAsyncThunk(
	'updateMealOrder',
	async payload => envAwarePost(
		'api/events/',
		{action:'updateMealOrder',...payload}
	)
)

export const groupFoodsIntoMeal = createAsyncThunk(
	'groupFoodsIntoMeal',
	async payload => await envAwarePost(
		'api/events/',
		{
			action:'groupFoodsIntoMeal',
			include_headings:1,
			...payload
		}
	)
)

export const ungroupMeal = createAsyncThunk(
	'ungroupMeal',
	async payload => await envAwarePost(
		'api/events/',
		{
			action:'ungroupMeal',
			...payload
		}
	)
)

export const editMeal = createAsyncThunk(
	'editMeal',
	async payload => envAwarePost(
		'api/events/',
		{action:'editMeal',...payload}
	)
)

export const createMeal = createAsyncThunk(
	'createMeal',
	async ({recipe_id,recipe_source='custom'},{getState})=>{
		const state = getState()
		const {
			tgtResourceId:nutrition_id,
			insertPosition
		} = state.resourceLibrary
		return envAwarePost(
			'api/events/',
			{
				action:'createMeal',
				routine_order:Number(insertPosition),
				nutrition_id,
				recipe_id,
				recipe_source
			}//end payload
		)//end return 
	}//end thunk cb
)

export const removeMeal = createAsyncThunk(
	'removeMeal',
	async payload => envAwarePost(
		'api/events/',
		{action:'removeMeal',...payload}
	)
)

export const removeNutritionItems = createAsyncThunk(
	'removeNutritionItems',
	async (payload) => await envAwarePost(
		'api/events/',
		{action:'removeNutritionItems',...payload}
	)
)

export const createNutritionHeading = createAsyncThunk(
	'createNutritionHeading',
	async payload => await envAwarePost(
		'api/events/',
		{action:'createNutritionHeading',...payload}
	)
)

export const updateFoodOrder = createAsyncThunk(
	'updateFoodOrder',
	async payload => await envAwarePost(
		'api/events/',
		{action:'updateFoodOrder',include_headings:1,...payload}
	)
)

export const editNutritionHeading = createAsyncThunk(
	'editNutritionHeading',
	async payload => await envAwarePost(
		'api/events/',
		{action:'editNutritionHeading',...payload}
	)
)

export const removeNutritionHeadings = createAsyncThunk(
	'removeNutritionHeadings',
	async payload => await envAwarePost(
		'api/events/',
		{action:'removeNutritionHeadings',...payload}
	)
)

export const getEventsLinkedToProgram = createAsyncThunk(
	'getEventsLinkedToProgram',
	async program_id => await envAwarePost(
		'api/programs/',
		{action:'getEventsLinkedToProgram',program_id}
	)
)

export const createRoutineHeading = createAsyncThunk(
	'events/createRoutineHeading',
	async payload => envAwarePost('api/events/api',{
		action:'createRoutineHeading',
		...payload
	})
)

export const editRoutineHeading = createAsyncThunk(
	'events/editRoutineHeading',
	async payload => envAwarePost('api/events/api',{
		action:'editRoutineHeading',
		...payload
	})
)

export const removeRoutineHeadings = createAsyncThunk(
	'events/removeRoutineHeadings',
	async payload => envAwarePost('api/events/api',{
		action:'removeRoutineHeadings',
		...payload
	})
)

export const openComments = createAsyncThunk(
	'events/openComments',
	async (payload,{dispatch}) => {
		//notify api that all comments have been seen
		envAwarePost('api/events/api/',{
			'action':'markEventCommentsSeen',
			'event_type':payload.type,
			'event_id':payload.id,
		})
		//update the event locally to set unseen comments count to 0
		dispatch(resetUnseenCommentsCount({
			'event_type':payload.type,
			'event_id':payload.id,
		}))
		//use existing functionality to open the comments
		dispatch(updateComments(payload))
	}
)

export const createEventCommment = createAsyncThunk(
	'events/createComment',
	async payload => envAwarePost('api/events/api/',{
		action:'createEventCommment',
		...payload
	})
)

export const removeExercisesFromWorkout = createAsyncThunk(
	'events/removeExercisesFromWorkout',
	async payload => envAwarePost('api/events/api/', {
		action: 'removeExercisesFromWorkout',
		...payload
	})
)

export const addItemToEvent = createAsyncThunk(
	'events/addItemToEvent',
	async payload => envAwarePost('api/events/api/', {
		action: 'addItemToEvent',
		...payload
	})
)

export const addExerciseToWorkout = createAsyncThunk(
	'events/addExerciseToWorkout',
	async payload => envAwarePost('api/events/api/', {
		action: 'addExerciseToWorkout',
		...payload
	})
)

export const replaceEventItem = createAsyncThunk(
	'events/replaceEventItem',
	async payload => envAwarePost('api/events/api/', {
		action: 'replaceEventItem',
		use_meal_groups:1,
		include_headings:1,
		return_event: 1,
		...payload
	})
)

export const updateEvent = createAsyncThunk(
	'events/updateEvent',
	async payload => await envAwarePost('api/events/api/', {
		action: 'updateEvent',
		...payload
	})
)

export const trackExerciseResults = createAsyncThunk(
	'events/trackExerciseResults',
	async payload => await envAwarePost(
		'api/events/api/',
		{
			action: 'trackExerciseResults',
			...payload
		}
	)
)

export const autoFillExerciseSets = createAsyncThunk(
	'events/autoFillExerciseSets',
	async payload => await await envAwarePost(
		'api/events/api/',
		{
			action: 'autoFillExerciseSets',
			...payload
		}
	)
)

export const updateExerciseSet = createAsyncThunk(
	'events/updateExerciseSet',
	async (payload,{getState,dispatch}) => {
		//local helper function
		const allValuesIdentical = (set_data,key)=>{
			//if updating rep_range check if all assigned reps
			//are the same else use the provided key
			key = (
				key === 'rep_range' || key === 'amrap'
				? 'assigned_reps'
				: key
			)
			//key will be something like
			//assinged_reps, result_reps
			//split these into variables like
			//valueType = "assigned"
			//valueKey = "reps"
			const [valueType,valueKey] = key.split('_')

			for (let i = 1; i < set_data.length; i++) {
				if(
					set_data[i-1][valueType][valueKey] !=
					set_data[i][valueType][valueKey]
				) return false
			}
			return true
		}
		//get state and check if we need to perform autofill
		const state = getState()
		const {exercise_id,workout_id,id:set_id,type,update} = payload
		const routine = state.allEvents.workouts.entities[workout_id].routine
		const exercise = extractExerciseFromRoutine(routine,type,exercise_id)
		//check if exercise needs auto fill
		//get idx of the set getting updated
		const idx = exercise.set_data.findIndex( set => set.id === set_id )
		const updateKey = Object.keys(payload.update)[0]
		//if idx is 0 and all values are the same then issue an autofill request
		//and don't auto fill for results tracking
		if(
			idx === 0 && 
			allValuesIdentical(exercise.set_data,updateKey) && 
			updateKey !== 'complete' && 
			!updateKey.includes('result')
		){
			//create a new payload for the auto fill update
			const ids = exercise.set_data.map( set => set.id )
			const autoFillPayload = {
				...payload,
				ids
			}
			dispatch(updateManySets({
				set_ids:ids,
				workout_id,
				exercise_id,
				type,
				update,
			}))
			return await envAwarePost(
				'api/events/api/',
				{
					action: 'autoFillExerciseSets',
					...autoFillPayload
				}
			)
		}
		//else update just this set
		else {
			dispatch(updateManySets({
				set_ids:[set_id],
				workout_id,
				exercise_id,
				type,
				update,
			}))
			return await envAwarePost(
				'api/events/api/',
				{
					action: 'updateExerciseSet',
					...payload
				}
			)
		}
	}
)

export const addExerciseResultVideo = createAsyncThunk(
	'events/addExerciseResultVideo',
	async payload => await envAwareFormSubmit({
		path:'api/events/api/',
		payload:{
			action:'addExerciseResultVideo',
			...payload
		}
	})
)

export const updateExerciseSetCount = createAsyncThunk(
	'events/updateExerciseSetCount',
	async payload => await envAwarePost(
		'api/events/api/',
		{
			action: 'updateExerciseSetCount',
			...payload
		}
	)
)

export const updateSupersetSetCount = createAsyncThunk(
	'events/updateSupersetSetCount',
	async payload => await envAwarePost(
		'api/events/api/',
		{
			action: 'updateSupersetSetCount',
			...payload
		}
	)
)

export const addEventNote = createAsyncThunk(
  'events/addEventNote', 
  async payload => envAwarePost(
    'api/events/api/',
    {
      action:'addEventNote',
      ...payload
    }
  )
)

export const deleteEvents = createAsyncThunk(
	'events/deleteEvents',
	async (payload, { dispatch,getState }) => {
		const state = getState()
		const {note,workout,task,nutrition} = state.allEvents.toDelete
		dispatch(deleteNotes({ids:note}))
		const res = await dispatch(deleteEventBatches({workout,task,nutrition}))
		return res.payload
	}
)

export const deleteEventBatches = createAsyncThunk(
	'events/deleteEventBatches',
	async (payload, { dispatch,getState }) => {
		const state = getState()
		const eventRes = await envAwarePost(
			'api/events/api/',
			{
				action: 'deleteEventBatches',
				...payload
			}
		)
		return eventRes;
	}
)

export const deleteItem = createAsyncThunk(
	'events/deleteItem',
	async payload => await envAwarePost('api/events/api/', {
		action: 'deleteItem',
		...payload
	})
)

export const deleteSuperset = createAsyncThunk(
	'events/deleteSuperset',
	async payload => await envAwarePost('api/events/api/', {
		action: 'deleteSuperset',
		...payload
	})
)

export const splitSuperset = createAsyncThunk(
	'events/splitSuperset',
	async payload => await envAwarePost('api/events/api/', {
		action: 'splitSuperset',
		...payload
	})
)

export const getEvents = createAsyncThunk(
	'events/get',
	async (payload,{dispatch}) => {
		const {client_id} = payload
		await dispatch(getAllNotes({client_id}))
		const res = await envAwarePost('api/events/api/', {
			action: 'getEventList',
			...payload
		})
		return res
	}
)

export const updateNutrtionSimplifiedFood = createAsyncThunk(
	'updateNutrtionSimplifiedFood',
	async payload => await envAwarePost('api/events/api/', {
		action: 'updateNutritionSimplifiedFood',
		...payload
	})
)

export const updateExerciseDetails = createAsyncThunk(
	'events/updateExerciseDetails',
	async payload => await envAwarePost('api/events/api/', {
		action: 'updateExerciseDetails',
		...payload
	})
)

export const appendExerciseWeight = createAsyncThunk(
	'events/appendExerciseWeight',
	async (payload, { getState }) => {
		const state = getState()
		const { workout_id, exercise_id, weight } = payload
		const workout = state.allEvents.workouts.entities[workout_id]
		const exercise = extractExerciseFromRoutine(workout.routine, 'exercise', exercise_id)
		//new weight was already added in pending action
		//so we can just pull the weights off the exercise
		//and send that to the API
		const weights = exercise.weights
		return await envAwarePost('api/events/api/', {
			action: 'updateExerciseDetails',
			exercise_type: 'exercise',
			item_id: exercise_id,
			weights
		})
	}
)

export const replaceExerciseWeight = createAsyncThunk(
	'events/replaceExerciseWeight',
	async (payload) => {
		const { exercise_id, weights } = payload
		return await envAwarePost('api/events/api/', {
			action: 'updateExerciseDetails',
			exercise_type: 'exercise',
			item_id: exercise_id,
			weights
		})
	}
)

export const updateFoodDetails = createAsyncThunk(
	'events/updateFoodDetails',
	async payload => await envAwarePost('api/events/api/', {
		action: 'updateFoodDetails',
		...payload
	})
)

export const makeNutritionSimplified = createAsyncThunk(
	'events/makeNutritionSimplified',
	async payload => envAwarePost('api/events/api/', {
		action: 'makeNutritionSimplified',
		...payload
	})
)

export const makeNutritionStandard = createAsyncThunk(
	'events/makeNutritionStandard',
	async payload => envAwarePost('api/events/api/', {
		action: 'makeNutritionStandard',
		...payload
	})
)

export const updateRoutineOrder = createAsyncThunk(
	'events/updateRoutineOrder',
	async payload => await envAwarePost('api/events/api/', {
		action: 'updateRoutineOrder',
		...payload
	})
)

export const copyEventToOtherClients = createAsyncThunk(
	'events/copyEventToOtherClients',
	async payload => await envAwarePost('api/events/api/', {
		action: 'copyEventToOtherClients',
		return_all_created: 1,
		...payload
	})
)

export const copyEventToSelf = createAsyncThunk(
	'events/copyEventToSelf',
	async payload => await envAwarePost('api/events/api/', {
		action: 'copyEventToSelf',
		...payload
	})
)

export const copyAndEdit = createAsyncThunk(
	'events/copyAndEdit',
	async payload => await envAwarePost('api/events/api/', {
		action: 'copyAndEdit',
		...payload
	})
)

export const addEventToFavorites = createAsyncThunk(
	'events/addEventToFavorites',
	async payload => await envAwarePost('api/events/api/', {
		action: 'addEventToFavorites',
		...payload
	})
)

export const getFavoriteDetails = createAsyncThunk(
	'getFavoriteDetails',
	async (payload) => envAwarePost('eventDetailsMobWeb/',{
		client_id: payload.clientID,
		display: payload.eventID,
		eventType: payload.eventType,
		showEventDetails: "1"
	})
)

export const getEventDetails = createAsyncThunk(
	'events/getDetails',
	async payload => {
		//workouts have a new api call following a new standard
		if (payload.event_type === 'workout') {
			const res = await envAwarePost('api/events/api/', {
				action: 'getWorkout',
				workout_id:payload.event_id
			})
			//the new api call follows a new standard.
			//so map the new api call back to the expected form
			//in the reducer for now. 
			res.data = res.data.workout
			//loop over the routine and add the client_id to each exercise
			res.data.routine.forEach(group=>{
				if(group.type==='heading') return
				group.exercises.forEach(exercise=>{
					exercise.client_id = res.data.client_id
				})
			})
			return res
		} else {
			return await envAwarePost('api/events/api/', {
				action: 'getEventDetails',
				//affects nutrition only
				use_meal_groups:1,
				include_headings:1,
				include_nutrition_meal_photos:1,
				...payload
			})
		}	
	}
)

export const applyAdditionalOptions = createAsyncThunk(
	'events/applyAdditionalOptions',
	async payload => await envAwarePost('api/events/api/', {
		action: 'applyAdditionalOptions',
		...payload
	})
)

export const createSuperset = createAsyncThunk(
	'events/createSuperset',
	async payload => await envAwarePost('api/events/api/', {
		action: 'createSuperset',
		...payload
	})
)

export const groupSuperset = createAsyncThunk(
	'events/groupSuperset',
	async payload => await envAwarePost('api/events/api/', {
		action: 'groupSuperset',
		...payload
	})
)

export const ungroupSuperset = createAsyncThunk(
	'events/ungroupSuperset',
	async payload => await envAwarePost('api/events/api/', {
		action: 'ungroupSuperset',
		...payload
	})
)

export const swapAssignedExercise = createAsyncThunk(
	'events/swapAssignedExercise',
	async payload => await envAwarePost('api/events/api/',{
		action:'swapAssignedExercise',
		...payload
	})
)

export const clearAssignedExerciseSets = createAsyncThunk(
	'events/clearAssignedExerciseSets',
	async payload => await envAwarePost(
		'api/events/api',
		{action:'clearAssignedExerciseSets',...payload}
	)
)

/** 
 * This action requires additionally handling when toggling 
 * from non-compact to compact. Each set in an exercise needs
 * to be updated to have the same value as the first set.
 */
export const toggleWorkoutCompactMode = createAsyncThunk(
	'events/toggleWorkoutCompactMode',
	async (workout_id,{dispatch,getState}) => {
		const state = getState()
		const workout = state.allEvents.workouts.entities[workout_id]
		//if toggling from non-compact to compact then we need to update sets
		const requiresSetsUpdate = workout.compact_mode === 0
		dispatch(updateEvent({
			event_type:'workout',
			event_id:workout_id,
			compact_mode:workout.compact_mode^1,
			is_playable:workout.compact_mode^1?0:1
		}))
		if(requiresSetsUpdate){
			workout.routine.forEach((item)=>{
				if(item.type==='heading') return
				const {exercises} = item
				exercises.forEach(exercise=>{
					const ids = exercise.set_data.map(set=>set.id)
					const firstSet = exercise.set_data[0]
					//generate a flattened update object
					//will be like {assigned_reps:3,...}
					const update = {}
					const valueTypes = ['assigned','result']
					valueTypes.forEach((valueType)=>{
						Object.entries(firstSet[valueType]).forEach(([assignable,val])=>{
							update[`${valueType}_${assignable}`] = val
						})
					})
					//update sets on the front end
					dispatch(updateManySets({
						exercise_id:exercise.exercise_id,
						type:exercise.type,
						set_ids:ids,
						workout_id,
						update,
					}))
					//issue api call to persist the update to all sets
					const autoFillPayload = {
						exercise_id:exercise.exercise_id,
						type:exercise.type,
						workout_id,
						update,
						ids
					}
					envAwarePost(
						'api/events/api/',
						{
							action: 'autoFillExerciseSets',
							...autoFillPayload
						}
					)//end envAwarePost
				})//end exercises.foreach
			})//end routine.foreach
		}
	}
)

export const createProgramEventCopy = createAsyncThunk(
	'events/createProgramEventCopy',
	async payload => await envAwarePost(
		'api/events/api',
		{action:'createProgramEventCopy',...payload}
	)
)

export const removeEventSeries = createAsyncThunk(
	'removeEventSeries',
	async payload => await envAwarePost(
		'api/events/',
		{action:'removeEventSeries',...payload}
	)
)

const workoutsAdapter = createEntityAdapter({
	selectId: ent => ent.workout_id
})

const tasksAdapter = createEntityAdapter({
	selectId: ent => ent.task_id
})

const nutritionAdapter = createEntityAdapter({
	selectId: ent => ent.nutrition_id
})

const notesAdapter = createEntityAdapter({
	selectId:ent=>ent.note_id
})

const getEventTypeAdapter = type => ({
	workout: workoutsAdapter,
	task: tasksAdapter,
	nutrition: nutritionAdapter
}[type])

export const getEventTypeKey = type => type === 'nutrition' ? type : `${type}s`

const slice = createSlice({
	name: 'events',
	initialState: {
		//stores state related to copying events
		copy: {
			show: false,
			id: null,
			type: null,
			origin:null,
		},
		//stores state for selected event comments
		//currently we only need to store the id & show
		//in global state. The comments component will 
		//handle it's own state locally once it gets an id
		comments: {
			show: false,
			id: null,
			type: null
		},
		calendarClientSelected: null,
		defaultClientSelected: null,
		status: 'init',
		detailsLoading: false,
		toDelete: {
			workout:[],
			task:[],
			nutrition:[],
			note:[]
		},
		notesEditor:{
			show:false,
			id:null,
			date: null,
			note:'',
			clientId:null
		},
		preview: {
			show: false,
			exercise: false,
		},
		filters: {
			query: false,
			upcomingOnly: false,
			status: 'any'
		},
		sort: {
			by: 'date',
			desc: true
		},
		editor: {
			type: null,
			id: null,
			insertPosition:null,
			//only matters for foods/exercises
			//tasks have no sub-items
			showLibrary: false,
			//state for replacing exercises/foods 
			//with other exercises and foods
			replace: {
				id: null,
				//type is only used for exercises
				//cardio | exercise
				type: null
			},
			//for bulk delete and other functionality
			//that requires selecing multiple items we put
			//the list here
			selectedItems:[]
		},
		expandedData: {
			show: false,
			open: null,
			complete: null,
			notMet: null,
			clientName: null,
			title: null,
			location: null,
			linkText: null,
		},
		favoriteEventDetails: [],
		workouts: workoutsAdapter.getInitialState({
			status:'idle'
		}),
		tasks: tasksAdapter.getInitialState({
			status:'idle'
		}),
		nutrition: nutritionAdapter.getInitialState({
			status:'idle'
		}),
		notes:notesAdapter.getInitialState(),
		selectedEventInfo: {
			selectedEventTypeList : null,
			selectedEventTypeCalendar: null,
			selectedDisplay: null,
			selectedFilter: null,
			selectedClient: null,
			selectedTrainer_id: null, 
			isCalendarView: true,
			selectedDate: ''
		},
		programs: {
			show: false,
			status: "init"
		},
		calendarRefetch: false
	},
	reducers: {
		//when a calendar event is deleted we need to filter 
		//all of the associated workouts from the state
		filterTrainingSessions : (state,action) => {
			//get ids of all workouts that correspond to the calendar event
			const calendarId = action.payload
			const workoutIds = Object
				.values(state.workouts.entities)
				.filter(w=>parseInt(w.calendar_id) === parseInt(calendarId))
				.map(w=>w.workout_id)

			workoutsAdapter.removeMany(state.workouts,workoutIds)
		},
		updateNotesEditor : (state,action)=>{
			state.notesEditor = {
				...state.notesEditor,
				...action.payload
			}
		},
		clearFilters: (state, action) => {
			state.filters = {
				query: false,
				upcomingOnly: false,
				status: 'any'
			}
		},
		setResultsTracking: (state, action) => {
			const { type, id, tracking } = action.payload
			const adapter = getEventTypeAdapter(type)
			adapter.updateOne(
				state[getEventTypeKey(type)],
				{ id, changes: { tracking } }
			)
		},
		setShowDetails: (state, action) => {
			const { type, id, showDetails } = action.payload
			const adapter = getEventTypeAdapter(type)
			adapter.updateOne(
				state[getEventTypeKey(type)],
				{ id, changes: { showDetails } }
			)
		},
		toggleDeleteItem: (state, action) => {
			const {id:toggleId,itemType} = action.payload
			const items = state.toDelete[itemType]
			if (items.includes(toggleId)) {
				const update = items.filter(id => toggleId !== id)
				state.toDelete[itemType] = update
			} else {
				items.push(toggleId)
			}
		},
		setDeleteEvents: (state, action) => {
			const update = {
				workout:[],
				task:[],
				nutrition:[],
				note:[]
			}
			const typedIds = action.payload
			typedIds.forEach(({eventType,id})=>{
				update[eventType].push(id)
			})
			state.toDelete = update
		},
		updateComments: (state, action) => {
			state.comments = {
				...state.comments,
				...action.payload
			}
		},
		updateCopy: (state, action) => {
			state.copy = {
				...state.copy,
				...action.payload
			}
		},
		updateCalendarClientSelected: (state, action) => {
			state.calendarClientSelected = action.payload;
		},
		updateDefaultClientSelected: (state, action) => {
			state.defaultClientSelected = action.payload;
		},
		triggerCalendarRefetch : (state,action) => {
			state.calendarRefetch = action.payload.refetch;
		},
		addEventForCopying: (state, action) => {
			let event = action.payload
			let event_type = action.payload.event_type
			//update state
			getEventTypeAdapter(event_type)
				.upsertMany(state[getEventTypeKey(event_type)], [event])
		},
		updateFilters: (state, action) => {
			state.filters = {
				...state.filters,
				...action.payload
			}
		},
		updateSelectedEventInfo : (state,action) => {
			state.selectedEventInfo = {
				...state.selectedEventInfo, 
				...action.payload
			}
		},
		updatePrograms: (state,action) => {
			state.programs = {
				...state.programs,
				...action.payload
			}
		},
		updateSort: (state, action) => {
			state.sort = {
				...state.sort,
				...action.payload
			}
		},
		updatePreview: (state, action) => {
			state.preview = {
				...state.preview,
				...action.payload
			}
		},
		updateEditor: (state, action) => {
			state.editor = {
				...state.editor,
				...action.payload
			}
		},
		updateExpandedData: (state, action) => {
			state.expandedData = {
				...state.expandedData, ...action.payload
			}
		},
		toggleExpandedData: (state, action) => {
			state.expandedData.show = !state.expandedData.show
		},
		updateManySets : (state,action) => {
			try {
				const {workout_id,exercise_id,set_ids,type,update} = action.payload
				const workout = state.workouts.entities[workout_id]
				const routine = workout.routine
				const exercise = extractExerciseFromRoutine(routine,type,exercise_id)
				exercise.has_results = Number(Object.keys(update).reduce((carry,key)=>(
					carry || key.includes('result_')
				),false))
				exercise.set_data
				.filter( s => set_ids.includes(s.id) )
				.forEach( set => {
					Object.entries(update).forEach( ([k,v]) => {
						//key will be something like
						//assinged_reps, result_reps
						//split these into variables like
						//valueType = "assigned"
						//valueKey = "reps"

						//keys that are not nested
						const flatKeys = [
							'complete',
							'result_video_url',
							'result_video_notes',
							'result_video_seen',
							'amrap',
							'rep_range',
						]

						if(flatKeys.includes(k)){
							set[k] = v
						}else{
							const [valueType,valueKey] = splitOnce(k,'_')
							set[valueType][valueKey] = v
						}
						
					})
				})
				//recalculate per-workout compliance
				let total = 0
				let completed = 0
				let isCompact = workout.compact_mode === 1
				routine.forEach(group=>{
					if (group.type==='heading') return
					group.exercises.forEach(exercise=>{
						if(isCompact){
							total += 1
							completed += exercise.complete
						}else{
							exercise.set_data.forEach(set=>{
								total += 1
								completed += set.complete
							})//end foreach set
						}
					})//end foreach exercise
				})//end foreach group
				const percent = total > 0 ? completed/total*100 : 0
				workout.compliance.total = total
				workout.compliance.completed = completed
				workout.compliance.percent = percent
			} catch(err) {
				console.error(err)
			}
		},
		resetUnseenCommentsCount : (state,action) => {
			const {event_type,event_id} = action.payload
			const adapter = getEventTypeAdapter(event_type)
			adapter.updateOne(
				state[getEventTypeKey(event_type)],
				{
					id:event_id,
					changes:{unseen_comments_count:0}
				}
			)
		}
	},
	extraReducers: {
		[updateTrackedExerciseItems.pending] : (state,action)=>{
			const {id,type,workoutId,tracked_items} = action.meta.arg
			const workout = state.workouts.entities[workoutId]
			const exercise = extractExerciseFromRoutine(workout.routine,type,id)
			exercise.tracked_items = tracked_items
		},

		[addExerciseResultVideo.fulfilled] : (state,action)=>{
			const {workout_id,exercise_id,id,type} = action.meta.arg
			const {result_video_url} = action.payload.data.set
			const routine = state.workouts.entities[workout_id].routine
			const exercise = extractExerciseFromRoutine(routine,type,exercise_id)
			exercise.set_data
			.filter( s => id === s.id )
			.forEach( set => {
				set.result_video_url = result_video_url
			})
		},

		[deleteEventBatches.pending]: (state, action) => {
			const eventTypes = ['workout','task','nutrition']
			eventTypes.forEach(eventType=>{
				const adapter = getEventTypeAdapter(eventType)
				adapter.removeMany(
					state[getEventTypeKey(eventType)], 
					state.toDelete[eventType]
				)
			})
			//clear out list
			state.toDelete = {
				workout:[],
				task:[],
				nutrition:[],
				note:[]
			}
		},

		[addEventNote.pending] : (state,action)=>{
			const {note,event_id,event_type} = action.meta.arg
			state[getEventTypeKey(event_type)].entities[event_id].client_note = note
		},

		[getEvents.pending]: state => { 
			state.status = 'loading' 
		},

		[getEvents.fulfilled]: (state, action) => {
			//get data from action
			const { event_type } = action.meta.arg
			const { success, data: { events } } = action.payload
			if (success!=='1') return 
			//update state
			state.status = 'loaded'
			getEventTypeAdapter(event_type).upsertMany(
				state[getEventTypeKey(event_type)], 
				//any events that were opened need to have some flags
				//updated so that they refetch correctly
				events.map(e=>{
					e.detailsPending = false
					e.hasDetails = false
					return e
				})//end events.map
			)//end upsert many
		},

		[getEventDetails.pending]: (state,action) => { 
			const {event_type,event_id} = action.meta.arg
			const event = state[getEventTypeKey(event_type)].entities[event_id]
			if(event) event.detailsPending = true
			state.detailsLoading = true 
		},

		[getEventDetails.fulfilled]: (state, action) => {
			const { event_type, event_id } = action.meta.arg
			const { success, data } = action.payload
			state.detailsLoading = false
			if(parseInt(success)){
				data.hasDetails = true
				data.detailsPending = false
				getEventTypeAdapter(event_type)
					.upsertOne(
						state[getEventTypeKey(event_type)],
						data
					)
			}
		},

		[getFavoriteDetails.fulfilled] : (state,action)=>{
			state.status = 'loaded'
			const {success, data} = action.payload
				if (success && action.payload.data) {
					state.favoriteEventDetails = data[0]
				}
		},

		[trackExerciseResults.pending]: (state, action) => {
			const { workout_id, exercise_id, type, trackable } = action.meta.arg
			//get workout
			const workout = state.workouts.entities[workout_id]
			//find exercise to update & update it
			workout.routine.forEach( item => {
				if(item.type==='heading') return
				item.exercises.forEach(e => {
					if (e.type === type && e.exercise_id === exercise_id) {
						const resultKey = trackable !== 'complete' ? `${trackable}Result` : trackable
						e[resultKey] = action.meta.arg[trackable]
					}
				})
			})

			//recalculate per-workout compliance
			let total = 0
			let completed = 0
			let isCompact = workout.compact_mode === 1
			workout.routine.forEach(group=>{
				if (group.type==='heading') return
				group.exercises.forEach( exercise => {
					if (isCompact) {
						total += 1
						completed += exercise.complete
					} else {
						exercise.set_data.forEach( set => {
							total += 1
							completed += set.complete
						})//end foreach set
					}
				})//end foreach exercise
			})//end foreach group
			const percent = total > 0 ? completed/total*100 : 0
			workout.compliance.total = total
			workout.compliance.completed = completed
			workout.compliance.percent = percent
		},
		/* -- update event -- */
		[updateEvent.pending]: (state, action) => {
			const { event_type, event_id } = action.meta.arg
			//loop over possible keys that could be set
			//and update the state with the new value
			const eventKeys = ['complete', 'title', 'notes', 'link', 'date','feature_media','thumbnail','compact_mode','file','meal_plan_type_id']
			eventKeys.forEach(key => {
				if (key in action.meta.arg) {
					const val = action.meta.arg[key]
					state[getEventTypeKey(event_type)].entities[event_id][key] = val
				}
			})
			const event = state[getEventTypeKey(event_type)].entities[event_id]
			//make sure that compliance is using the correct unit based on compact mode
			if(event_type === 'workout'&&event.hasDetails){
				const isCompact = event.compact_mode === 1
				event.compliance.unit = isCompact ? 'exercise' : 'set'
			}
		},

		[updateEvent.fulfilled]: (state,action)=>{
			const {success,message} = action.payload
			if(success === '0') alert(message)
		},

		/* -- update simplified food -- */
		[updateNutrtionSimplifiedFood.pending]: (state, action) => {
			const { id, completed, quantity, nutrition_id } = action.meta.arg
			const toUpdate = state.nutrition.entities[nutrition_id].food.find(f => f.id === id)
			if (completed) toUpdate.completed = completed
			if (quantity) toUpdate.quantity = quantity
		},

		[updateNutrtionSimplifiedFood.fulfilled]: (state, action) => {
			//console.log(action.payload)
		},

		[copyEventToOtherClients.fulfilled]: (state, action) => {
			const { event_type } = action.meta.arg
			getEventTypeAdapter(event_type).upsertMany(
				state[getEventTypeKey(event_type)],
				action.payload.data
			)
		},

		[copyEventToSelf.fulfilled]: (state, action) => {
			const { event_type } = action.meta.arg
			getEventTypeAdapter(event_type).upsertOne(
				state[getEventTypeKey(event_type)],
				action.payload.data
			)
		},

		[addEventToFavorites.fulfilled]: (state, action) => {
			const { event_type } = action.meta.arg
			const {success,message} = action.payload
			if(success==='1'){
				getEventTypeAdapter(event_type).upsertOne(
					state[getEventTypeKey(event_type)],
					action.payload.data
				)
			}else{
				alert(message)
			}
		},

		[addItemToEvent.pending]: (state,action)=>{
			const {event_id:nutrition_id,name} = action.meta.arg
			const nutrition = state.nutrition.entities[nutrition_id]
			const routine_order = (
				Boolean(action.meta.arg.routine_order)
				? action.meta.arg.routine_order
				: nutrition.food.length
			)
			const food = nutrition.food
			const placeholder = {
				type:'placeholder',
				text:`Adding ${name}`,
				routine_order:routine_order,
			}
			nutrition.food = [
				...food.slice(0,routine_order-1),
				placeholder,
				...food.slice(routine_order-1).map(item=>({
					...item,
					routine_order:item.routine_order+1
				}))
			]
		},

		[addItemToEvent.fulfilled]: (state, action) => {
			const {success,message} = action.payload
			if(success === '0'){
				alert(message)
				return 
			}
			const { event_id, exercise_type, event_type } = action.meta.arg
			if (event_type === 'workout') {
				const workout = state.workouts.entities[event_id]
				const exercise = action.payload.data
				const newRoutine = [...workout.routine, {
					type: exercise_type === 'exercise' ? 'exerciseSingleton' : 'cardioSingleton',
					exercises: [exercise]
				}].sort((a,b)=>(
					a.exercises[0].routine_order < b.exercises[0].routine_order ? -1 : 1
				))
				workout.routine = newRoutine
			} else {
				const nutrition = state.nutrition.entities[event_id]
				const food = {
					...action.payload.data,
					type:'food'
				}
				food.name = food.food_name
				nutrition.food = nutrition.food.map( item => {
					//replace the placeholder for the new food
					if (item.routine_order===food.routine_order) {
						return food
					}
					return item
				})
			}
		},

		[addExerciseToWorkout.pending] : (state,action) => {

			const {
				name,
				workout_id,
				routine_order,
				after_heading,
				superset
			} = action.meta.arg

			//insert a placeholder item and increment all routine order values
			//after the insert position
			const placeholder = {
				type:'placeholder',
				id:Date.now(),
				routine_order,
				workout_id,
				name,
				superset
			}

			const workout = state.workouts.entities[workout_id]

			workout.routine.forEach( group => {
				if ( ['placeholder'].includes(group.type) ) return 
				if ( group.type === 'heading' ) {
					if ( group.routine_order > routine_order ) group.routine_order += 1
					if ( group.routine_order === routine_order && !Boolean(after_heading)) group.routine_order += 1
				} else {
					group.exercises.forEach( e => {
						if ( e.routine_order >= routine_order ) e.routine_order += 1
					})
				}
			})

			//add placeholder into the main routine
			if ( !Boolean(superset) ) {
				//need to account for supersets since these will have groups of exerciess
				//that span some range of routine orders (eg 3-6). Thus, to insert a placeholder
				//into the routine array the insert position must be calculated.
				let insertPosition = 0
				workout.routine.forEach( group => {
					if ( group.type === 'placeholder' ) {
						if (group.routine_order < routine_order) {
							insertPosition+=1
						}
					} else if ( group.type !== 'heading' ) {
						if(group.exercises[0].routine_order < routine_order){
							insertPosition+=1
						}
					} else if ( group.type === 'heading' ) {
						if(
							group.routine_order < routine_order 
							|| (
								group.routine_order == routine_order 
								&& Boolean(after_heading)
							) 
						){
							insertPosition+= 1
						}
					}
					
				})
				workout.routine.splice(insertPosition,0,placeholder)
			}
			//add a placeholder into the superset
			else {
				//loop over all groups and splice into the correct superset
				workout.routine.forEach( group => {
					if(group.type==='superset'&&group.exercises[0].superset===superset){
						let insertPosition = 0
						group.exercises.forEach(e=>{
							if (e.routine_order < routine_order) insertPosition+=1
						})
						group.exercises.splice(insertPosition,0,placeholder)
					}
				})
			}

			if(state.editor.insertPosition !== null){
				state.editor.insertPosition += 1
			}
		},

		[addExerciseToWorkout.fulfilled]: (state, action) => {
			const {success,message} = action.payload

			if ( success === '0' ) {
				alert(message)
				return 
			}

			const { workout_id } = action.meta.arg
			const workout = state.workouts.entities[workout_id]
			const {exercise,routine} = action.payload.data
			//adding to a superset vs adding into the main routine require 
			//different handling
			if (exercise.superset === 0) {
				//find the routine placeholder
				let insertIdx = 0
				workout.routine.forEach((routineItem,i)=>{
					if(
						routineItem.type === 'placeholder' &&
						routineItem.routine_order === exercise.routine_order
					){
						insertIdx = i
					}
				})
				//replace the placeholder with a new group
				workout.routine[insertIdx] = {
					id:Date.now(),
					type:`${exercise.type}Singleton`,
					sets:1,
					setsResult:0,
					has_results:0,
					exercises:[exercise]
				}
			} else {
				//replace the placeholder in the superset with the new exercise
				workout.routine.forEach(routineItem=>{
					//if group is where placeholder is locacted
					if(
						routineItem.type === 'superset' &&
						routineItem.exercises[0].superset === exercise.superset
					){
						//find the index of the placeholder
						let insertIdx = 0
						routineItem.exercises.forEach((supersetExercise,i)=>{
							if(
								supersetExercise.type === 'placeholder' &&
								supersetExercise.routine_order === exercise.routine_order
							){
								insertIdx = i
							}
						})
						//replace the placeholder
						routineItem.exercises[insertIdx] = exercise
					}
				})//end workout.routine.foreach
			}
			
		},

		[removeExercisesFromWorkout.pending]: (state, action) => {
			const { exercises, workout_id } = action.meta.arg
			const workout = state.workouts.entities[workout_id]
			workout.routine = workout.routine.map(
				group => {
					if(group.type!=='heading'){
						group.exercises = group.exercises.filter(exercise=>{
							for (var i = exercises.length - 1; i >= 0; i--) {
								const {type,id} = exercises[i]
								//if group is selected do not includ it
								if(
									(exercise.type === type) &&
									(exercise.exercise_id === id)
								){
									return false
								}
							}
							return true
						})
					}
					return group
				}
			).filter(group=>group.type==='heading'||group.exercises.length>0)//end filter
			//correct routine_orders
			let routine_order = 1
			workout.routine.forEach(routineItem=>{
				if(routineItem.type==='heading'){
					routineItem.routine_order = routine_order
				}else{
					routineItem.exercises.forEach(exercise=>{
						exercise.routine_order = routine_order++
					})
				}
			})
		},

		[deleteItem.pending]: (state, action) => {
			const { item_id, event_id, exercise_type, event_type } = action.meta.arg
			if (event_type === 'workout') {
				const workout = state.workouts.entities[event_id]
				workout.routine = workout.routine.filter(
					group => (
						(group.exercises[0].type !== exercise_type) ||
						(group.exercises[0].exercise_id !== item_id)
					)//end group=>()
				)//end filter
			} else {
				const nutrition = state.nutrition.entities[event_id]
				nutrition.food = nutrition.food.filter(
					food => food.food_id !== item_id
				)//end filter
			}
		},

		[deleteItem.fulfilled] : (state,action)=>{
			const {success,message} = action.payload
			if(success === '0'){
				alert(message)
				return 
			}
		},

		[updateRoutineOrder.pending]: (state, action) => {
			const { event_id, event_type, routineUpdate, foodsUpdate } = action.meta.arg
			if (event_type === 'workout') {
				state.workouts.entities[event_id].routine = routineUpdate
			} else {
				state.nutrition.entities[event_id].food = foodsUpdate
			}
		},

		[deleteSuperset.pending]: (state, action) => {
			const { workout_id, superset } = action.meta.arg
			const workout = state.workouts.entities[workout_id]

			workout.routine = workout.routine.filter(group => (
				group.type !== 'superset' ||
				group.exercises[0].superset !== superset
			))
		},

		[createSuperset.fulfilled]: (state, action) => {
			const { data } = action.payload
			state.workouts.entities[data.workout_id] = data
		},

		[groupSuperset.pending] : (state,action) => {
			const {exercise_ids,workout_id} = action.meta.arg
			//Function: increment all subsequent supersets
			//(1) get max routine_order value from all exercises
			const max_routine_order = exercise_ids.reduce(
				(carry,exercise) => Math.max(carry,exercise.routine_order), 
				exercise_ids[0].routine_order
			)
			//(2) loop over workout and increment all superset values occuring after 
			//max_routine_order
			const workout = state.workouts.entities[workout_id]
			workout.routine.forEach( group => {
				if(group.type!=='heading'){
					group.exercises.forEach( exercise => {
						//increment superset values if needed
						if(
							exercise.routine_order > max_routine_order &&
							group.type === 'superset'
						){
							exercise.superset += 1
						}//end if
					})//end exercises.foreach
				}
			})//end workout.foreach
		},

		[groupSuperset.fulfilled]: (state, action) => {
			const {success,message} = action.payload
			if(success === '0'){
				alert(message)
				return 
			}
			const { data } = action.payload
			state.workouts.entities[data.workout_id] = data
		},

		[splitSuperset.fulfilled]: (state, action) => {
			const { data } = action.payload
			state.workouts.entities[data.workout_id] = data
		},

		[ungroupSuperset.pending] : (state,action) => {
			const {superset,workout_id} = action.meta.arg
			const workout = state.workouts.entities[workout_id]
			const routineUpdate = []
			let currentSuperset = 1
			workout.routine.forEach( (group,i) => {
				if (group.type === 'superset') {
					//split superset into single exercise groups
					if (currentSuperset === superset) {
						//add all individual exercises as singleton groups
						group.exercises.forEach( exercise => {
							exercise.superset = 0
							const newGroup = {
								exercises:[exercise],
								sets:exercise.sets,
								setsResults:exercise.result_sets,
								//give the group a unique id
								id:`exercise-group-${i}-${exercise.exercise_id}`,
								//set correct type for new group
								type:`${exercise.type}singleton`,
								has_results:Number(
									exercise.set_data.reduce(
										(set_carry,set) => set_carry || Object.values(set.result).reduce(
											(val_carry,value) => val_carry || Boolean(value),
											false
										),//end reduce result values
										false
									)
								)//end reduce set data
							}
							routineUpdate.push(newGroup)
						})//end exercises.foreach
					} else {
						//decrement all superset numbers for supersets occuring after
						//the one just ungrouped
						if(currentSuperset >= superset){
							group.exercises = group.exercises.map( exercise => {
								exercise.superset -= 1
								return exercise
							})
						}
						//add un affected superset to the update
						routineUpdate.push(group)
					}
					currentSuperset += 1
				} else {
					routineUpdate.push(group)
				}
			})//end workout.foreach
			workout.routine = routineUpdate
		},

		[ungroupSuperset.fulfilled] : (state,action) => {
			const {success,message} = action.payload
			if(success === '0'){
				alert(message)
				return 
			}
		},

		[replaceEventItem.fulfilled]: (state, action) => {
			const {success,message} = action.payload
			if(success === '0'){
				alert(message)
				return 
			}
			const { event_type } = action.meta.arg
			const { data } = action.payload
			if (event_type === 'workout') {
				state.workouts.entities[data.workout_id] = data
			} else {
				state.nutrition.entities[data.nutrition_id] = data
			}
		},

		[swapNutritionFood.fulfilled]: (state,action)=>{
			const {nutrition} = action.payload.data
			state.nutrition.entities[nutrition.nutrition_id] = nutrition
		},

		[swapAssignedExercise.fulfilled]: (state, action) => {
			const {success,message} = action.payload
			if(success === '0'){
				alert(message)
				return 
			}
			const { data:{workout} } = action.payload
			state.workouts.entities[workout.workout_id] = workout
		},

		[makeNutritionSimplified.pending]: (state, action) => {
			const { nutrition_id } = action.meta.arg
			const nutrition = state.nutrition.entities[nutrition_id]
			if (nutrition) {
				nutrition.meal_plan_type_id = 1
				nutrition.simplified = 1
			}
		},

		[makeNutritionSimplified.fulfilled]: (state, action) => {
			const { nutrition_id } = action.meta.arg
			const { data } = action.payload
			state.nutrition.entities[nutrition_id] = data
		},

		[makeNutritionStandard.pending]: (state, action) => {
			const { nutrition_id } = action.meta.arg
			const nutrition = state.nutrition.entities[nutrition_id]
			if (nutrition) {
				nutrition.meal_plan_type_id = 0
				nutrition.simplified = 0
			}
		},

		[makeNutritionStandard.fulfilled]: (state, action) => {
			const { nutrition_id } = action.meta.arg
			const { data } = action.payload
			state.nutrition.entities[nutrition_id] = data
		},

		[updateFoodDetails.pending]: (state, action) => {
			const { quantity,is_optional, food_id, nutrition_id,food_db_portion_id } = action.meta.arg
			if (quantity) {
				console.log(quantity,is_optional, food_id, nutrition_id,food_db_portion_id)
				const nutrition = state.nutrition.entities[nutrition_id]
				nutrition.food = nutrition.food.map(item=>{
					if (item.type==='meal') {
						item.foods = item.foods.map(food=>{
							if (food.food_id === food_id) {
								console.log('did this')
								food.quantity = quantity
							}
							return food
						})//end map meal foods
					}//end if meal
					else if (item.food_id === food_id) {
						item.quantity = quantity
					}//end if match root level food
					return item
				})
			}
			if (food_db_portion_id) {
				const nutrition = state.nutrition.entities[nutrition_id]
				nutrition.food = nutrition.food.map(item=>{
					if (item.type==='meal') {
						item.foods = item.foods.map(food=>{
							if (food.food_id === food_id) {
								food.food_db_portion_id = food_db_portion_id
							}
							return food
						})//end map meal foods
					}//end if meal
					else if (item.food_id === food_id) {
						item.food_db_portion_id = food_db_portion_id
					}//end if match root level food
					return item
				})
			}
			if(typeof is_optional !== 'undefined'){
				const nutrition = state.nutrition.entities[nutrition_id]
				nutrition.food = nutrition.food.map(item=>{
					if (item.type==='meal') {
						item.foods = item.foods.map(food=>{
							if (food.food_id === food_id) {
								food.is_optional = is_optional
							}
							return food
						})//end map meal foods
					}//end if meal
					else if (item.food_id === food_id) {
						item.is_optional = is_optional
					}//end if match root level food
					return item
				})
			}
		},

		[updateExerciseDetails.pending]: (state, action) => {
			const {
				item_id,
				workout_id,
				assignableKey,
				exercise_type,
				assignableValue,
			} = action.meta.arg
			const routine = state.workouts.entities[workout_id].routine
			//find and update exercise
			const exercise = extractExerciseFromRoutine(routine, exercise_type, item_id)
			exercise[assignableKey] = assignableValue
		},//end [updateExerciseDetails.pending]

		[updateExerciseDetails.fulfilled] : (state,action)=>{
			const {success,message} = action.payload
			if(success === '0') alert(message)
		},

		[appendExerciseWeight.pending]: (state, action) => {
			const {
				exercise_id,
				workout_id,
				weight
			} = action.meta.arg
			const routine = state.workouts.entities[workout_id].routine
			const exercise = extractExerciseFromRoutine(routine, 'exercise', exercise_id)
			if (exercise) exercise.weights.push(weight)
		},//end [appendExerciseWeight.pending]

		[replaceExerciseWeight.pending]: (state, action) => {
			const {
				exercise_id,
				workout_id,
				weights
			} = action.meta.arg
			const routine = state.workouts.entities[workout_id].routine
			const exercise = extractExerciseFromRoutine(routine, 'exercise', exercise_id)
			if (exercise) exercise.weights = weights
		},//end [replaceExerciseWeight.pending]

		[updateExerciseSetCount.fulfilled]: (state,action) => {
			const {success,message} = action.payload
			if(success === '0'){
				alert(message)
				return 
			}
			const {exercise} = action.payload.data
			const workout = state.workouts.entities[exercise.workout_id]
			//find exercise to update & update it
			workout.routine?.forEach(item => {
				if(item.type==='heading') return
				item.exercises?.forEach(e => {
					if (e.type === exercise.type && e.exercise_id === exercise.exercise_id) {
						Object.entries(exercise).forEach(([k,v])=>{
							e[k] = v
						})
					}
				})
			})
		},

		[updateExerciseSet.fulfilled]: (state,action) => {
			const {success,message} = action.payload
			if(success === '0'){
				alert(message)
				return 
			}
		},

		[updateSupersetSetCount.fulfilled]: (state,action) => {
			const {workout_id,superset,sets} = action.meta.arg
			const {update} = action.payload.data
			const workout = state.workouts.entities[workout_id]
			//find exercise to update & update it
			for (var i = workout.routine.length - 1; i >= 0; i--) {
				const item = workout.routine[i]
				if(item.type==='heading') continue
				if(item.exercises[0].superset === superset){
					item.sets = sets
					item.exercises = update
					break
				}
			}
		},

		[clearAssignedExerciseSets.fulfilled] : (state,action) =>{
			const {success,message} = action.payload
			if(success === '0'){
				alert(message)
				return 
			}
			const {exercise} = action.payload.data
			const workout = state.workouts.entities[exercise.workout_id]
			//find exercise to update & update it
			workout.routine.forEach(item => {
				if(item.type==='heading') return
				item.exercises.forEach(e => {
					if (e.type === exercise.type && e.exercise_id === exercise.exercise_id) {
						Object.entries(exercise).forEach(([k,v])=>{
							e[k] = v
						})
					}
				})
			})
		},

		[autoFillExerciseSets.pending] : (state,action) => {
			const {update,exercise_id,workout_id,ids,type} = action.meta.arg
			const routine = state.workouts.entities[workout_id].routine
			const exercise = extractExerciseFromRoutine(routine,type,exercise_id)
			exercise.has_results = Number(Object.keys(update).reduce((carry,key)=>(
				carry || key.includes('result_')
			),false))
			exercise.set_data
			.filter(s=>ids.includes(s.id))
			.forEach( exercise => {
				Object.entries(update).forEach( ([k,v]) => {
					//key will be something like
					//assinged_reps, result_reps
					//split these into variables like
					//valueType = "assigned"
					//valueKey = "reps"
					const [valueType,valueKey] = splitOnce(k,'_')
					exercise[valueType][valueKey] = v
				})
			})
		},

		[autoFillExerciseSets.fulfilled] : (state,action) => {
			const {success,message} = action.payload
			if(success === '0') alert(message)
		},

		[createProgramEventCopy.fulfilled] : (state,action) => {
			const {success,data} = action.payload
			if (success === '1') {
				const {event,src_event} = data
				//insert new event
				getEventTypeAdapter(event.event_type)
					.upsertOne(
						state[getEventTypeKey(event.event_type)],
						event
					)
				//update src event
				getEventTypeAdapter(src_event.event_type)
					.upsertOne(
						state[getEventTypeKey(src_event.event_type)],
						src_event
					)
			}//end if success
		},//end [createProgramEventCopy.fulfilled]

		[createEventCommment.fulfilled] : (state,action) => {
			const {event_type,event_id} = action.meta.arg
			const {comment} = action.payload.data
			const adapter = getEventTypeAdapter(event_type)
			const event = state[getEventTypeKey(event_type)].entities[event_id]
			event.comments.push(comment)
		},

		[createRoutineHeading.pending] : (state,action) => {
			const {superset} = state.editor
			const {workout_id,routine_order,after_heading} = action.meta.arg
			//insert a placeholder item and increment all routine order values
			//after the insert position
			const placeholder = {
				type:'placeholder',
				placeholder_type:'heading',
				id:Date.now(),
				routine_order,
				workout_id,
				name:'Heading',
				superset
			}

			const workout = state.workouts.entities[workout_id]

			//need to account for supersets since these will have groups of exerciess
			//that span some range of routine orders (eg 3-6). Thus, to insert a placeholder
			//into the routine array the insert position must be calculated.
			let insertPosition = 0
			workout.routine.forEach( group => {
				if(group.type==='placeholder'){
					if(group.routine_order < routine_order){
						insertPosition+=1
					}
				}else if(group.type!=='heading'){
					if(group.exercises[0].routine_order < routine_order){
						insertPosition+=1
					}
				}else if(group.type==='heading'){
					if(group.routine_order <= routine_order){
						insertPosition+=1
					}
				}
			})
			workout.routine.splice(insertPosition,0,placeholder)
		},

		[createRoutineHeading.fulfilled] : (state,action) => {
			const {workout_id} = action.meta.arg
			const {data:{workout}} = action.payload
			state.workouts.entities[workout_id] = workout
		},

		[removeRoutineHeadings.pending] : (state,action) => {
			const {workout_id,ids} = action.meta.arg
			const workout = state.workouts.entities[workout_id]
			workout.routine = workout.routine.filter(routineItem=>(
				routineItem.type !== 'heading' || !ids.includes(routineItem.id)
			))
		},

		[removeEventSeries.pending] : (state,action) => {
			const {event_type,recurring_id} = action.meta.arg
			const events = Object.values(state[getEventTypeKey(event_type)].entities)
			const adapter = getEventTypeAdapter(event_type)
			const toRemove = events
				.filter(e=>e.recurring===recurring_id)
				.map(e=>e[`${event_type}_id`])
			adapter.removeMany(state[getEventTypeKey(event_type)],toRemove)
		},

		[getEventsLinkedToProgram.fulfilled] : (state,action) => {
			const {events_by_type} = action.payload.data
			Object.entries(events_by_type)
			.filter(([type,events])=>type!=='on_demand_video')
			.forEach(([type,events])=>{
				getEventTypeAdapter(type).upsertMany(
					state[getEventTypeKey(type)],
					events
				)//end getEventTypeAdapter
			})//end entries
		},

		[createNutritionHeading.pending] : (state,action) => {
			const {nutrition_id,routine_order} = action.meta.arg
			const nutrition = state.nutrition.entities[nutrition_id]
			const food = nutrition.food
			const placeholder = {
				type:'placeholder',
				text:'Adding Heading',
				routine_order:routine_order,
			}
			nutrition.food = [
				...food.slice(0,routine_order-1),
				placeholder,
				...food.slice(routine_order-1).map(item=>({
					...item,
					routine_order:item.routine_order+1
				}))
			]
		},

		[createNutritionHeading.fulfilled] : (state,action) => {
			const {nutrition_heading} = action.payload.data
			nutrition_heading.type = 'heading';
			const {nutrition_id} = action.meta.arg
			const nutrition = state.nutrition.entities[nutrition_id]
			nutrition.food = nutrition.food.map((item)=>{
				if(item.routine_order===nutrition_heading.routine_order){
					return nutrition_heading
				}
				return item
			})
		},

		[removeNutritionHeadings.pending] : (state,action) => {
			const {nutrition_id,ids} = action.meta.arg
			const nutrition = state.nutrition.entities[nutrition_id]
			nutrition.food = nutrition.food.filter(food=>(
				food.type !== 'heading' || !ids.includes(food.id)
			))
			//correct all routine_orders
			nutrition.food = nutrition.food.map((food,i)=>({
				...food,
				routine_order:i+1
			}))
		},

		[updateFoodOrder.pending] : (state,action) => {
			const {nutrition_id,foodsUpdate} = action.meta.arg
			const nutrition = state.nutrition.entities[nutrition_id]
			nutrition.food = foodsUpdate
		},

		[updateNutritionOrder.pending] : (state,action) => {
			const {nutrition_id,foodsUpdate} = action.meta.arg
			const nutrition = state.nutrition.entities[nutrition_id]
			nutrition.food = foodsUpdate
		},

		[updateMealOrder.pending] : (state,action) => {
			const {nutrition_id,meal_id,foodsUpdate} = action.meta.arg
			const nutrition = state.nutrition.entities[nutrition_id]
			nutrition.food = nutrition.food.map(item=>{
				if (item.type==='meal'&&item.id===meal_id) {
					item.foods = foodsUpdate
				}
				return item
			})//end map
		},

		[removeNutritionItems.pending]: (state,action)=>{
			const {nutrition_id,items} = action.meta.arg

			//map from type to set of ids being removed
			const typeIdMap = {
				meal:new Set(),
				food:new Set(),
				heading: new Set()
			}

			items.forEach(item=>{
				typeIdMap[item.type].add(item.id)
			})

			const nutrition = state.nutrition.entities[nutrition_id]
			nutrition.food = nutrition.food.filter(item=>{
				if (item.type==='heading') {
					return !typeIdMap.heading.has(item.id)
				} else if(item.type==='food') {
					return !typeIdMap.food.has(item.food_id)
				} else if(item.type==='meal') {
					item.foods = item.foods.filter(food=>!typeIdMap.food.has(food.food_id))
					return !typeIdMap.meal.has(item.id)
				}
			})
			//correct all routine_orders
			nutrition.food = nutrition.food.map((item,i)=>({
				...item,
				routine_order:i+1
			}))
		},

		[createMeal.pending] : (state,action) => {
			const nutrition_id = state.editor.id
			const {name} = action.meta.arg
			const nutrition = state.nutrition.entities[nutrition_id]
			const routine_order = (
				Boolean(action.meta.arg.routine_order)
				? action.meta.arg.routine_order
				: nutrition.food.length
			)
			const food = nutrition.food
			const placeholder = {
				type:'placeholder',
				text:`Adding Recipe ${name}`,
				routine_order:routine_order,
			}
			nutrition.food = [
				...food.slice(0,routine_order-1),
				placeholder,
				...food.slice(routine_order-1).map(item=>({
					...item,
					routine_order:item.routine_order+1
				}))
			]
		},

		[createMeal.fulfilled] : (state,action) => {
			const {nutrition:{nutrition_id},meal} = action.payload.data
			const nutrition = state.nutrition.entities[nutrition_id]
			nutrition.food = nutrition.food.map((item,i)=>{
				if(item.type==='placeholder'&&i+1===meal.routine_order){
					return meal
				} else {
					return item
				}
			})
		},

		[removeMeal.pending] : (state,action) => {
			const {nutrition_id,id} = action.meta.arg
			const nutrition = state.nutrition.entities[nutrition_id]
			nutrition.food = nutrition.food.filter(item=>item.type!=='meal'||item.id!==id)
		},

		[removeFoodFromMeal.pending] : (state,action) => {
			const {nutrition_id,meal_id,food_id} = action.meta.arg
			const nutrition = state.nutrition.entities[nutrition_id]
			nutrition.food = nutrition.food.map(item=>{
				if(item.type==='meal'&&item.id===meal_id){
					item.foods = item.foods.filter(f=>f.food_id!==food_id)
				}
				return item
			})//end map
			nutrition.food = nutrition.food.map((item,i)=>{
				item.routine_order = i + 1
				return item
			})
		},

		[ungroupMeal.pending] : (state,action) => {
			const {nutrition_id,id} = action.meta.arg
			const nutrition = state.nutrition.entities[nutrition_id]
			const update = []
			let idx = 1
			nutrition.food.forEach((item)=>{
				if(item.type==='meal'&&item.id===id){
					item.foods.forEach(food=>{
						food.type = 'food'
						food.meal_id = 0
						food.routine_order = idx
						idx += 1
						update.push(food)
					})
				}//end if
				else {
					item.routine_order = idx
					idx += 1
					update.push(item)
				}//end else
			})//end foreach


			nutrition.food = update

		},

		[editMeal.pending] : (state,action) => {
			const {nutrition_id,id,update} = action.meta.arg
			//editing the recipe requires response from api 
			if (Boolean(update.recipe_id)) return 
			const nutrition = state.nutrition.entities[nutrition_id]
			nutrition.food = nutrition.food.map(item=>{
				if (item.type === 'meal' && item.id === id) {
					return {
						...item,
						...update
					}
				}
				return item
			})

		},

		[editMeal.fulfilled] : (state,action) => {
			const {update} = action.meta.arg
			const {meal} = action.payload.data
			//if recipe_id was not updated there is no need to 
			//apply any changes since all non-recipe updates are
			//applied in the editMeal.pending (ie optimistically)
			if (!Boolean(update.recipe_id)) return 
			const nutrition = state.nutrition.entities[meal.nutrition_id]
			nutrition.food = nutrition.food.map(item=>{
				if (item.type === 'meal' && item.id === meal.id) {
					return meal
				}
				return item
			})

		},

		[groupFoodsIntoMeal.fulfilled] : (state,action)=>{
			const {success} = action.payload
			if(parseInt(success)===1){
				const {food,nutrition_id} = action.payload.data.nutrition
				state.nutrition.entities[nutrition_id].food = food
			}
		},

		[addFoodToMeal.pending] : (state,action)=>{
			const nutrition_id = state.editor.id
			const {food_db_name,meal_id,routine_order} = action.meta.arg
			const nutrition = state.nutrition.entities[nutrition_id]
			const meal = nutrition.food.find(
				item => item.type==='meal' && item.id===meal_id
			)
			const foods = meal.foods
			const placeholder = {
				type:'placeholder',
				text:`Adding ${food_db_name}`,
				routine_order:routine_order,
			}
			meal.foods = [
				...foods.slice(0,routine_order-1),
				placeholder,
				...foods.slice(routine_order-1).map(item=>({
					...item,
					routine_order:item.routine_order+1
				}))
			]
		},

		[addFoodToMeal.fulfilled] : (state,action)=>{
			const {food,meal:{id:meal_id,nutrition_id}} = action.payload.data
			const nutrition = state.nutrition.entities[nutrition_id]
			const meal = nutrition.food.find(item=>item.type==='meal'&&item.id===meal_id)
			meal.foods = meal.foods.map((item,i)=>{
				if(item.type === 'placeholder'&&i+1===food.routine_order){
					return food
				}else {
					return item
				}
			})
		},

		[copySuperset.pending] : (state,action)=>{
			const {routine_order,workout_id,superset} = action.meta.arg
			const workout = state.workouts.entities[workout_id]
			const {routine} = workout
			const placeholder = {
				type:'placeholder',
				name:`Superset`,
				routine_order,
			}
			const update = []
			routine.forEach(item=>{
				update.push(item)
				if (
						item.type==='superset'
						&&
						item.exercises[0].superset === superset
				){
					update.push(placeholder)
				}
			})
			workout.routine = update
		},

		[copySuperset.fulfilled] : (state,action)=>{
			const {workout_id,routine} = action.payload.data.workout
			state.workouts.entities[workout_id].routine = routine
		},

		[fillExerciseWithLastAssignedData.fulfilled] : (state,action)=>{
			const {success} = action.payload
			if (success==='0') return
			const {exercise,workout} = action.payload.data
			workoutsAdapter.upsertOne(state.workouts,workout)
		},

		[logNutritionMealPhoto.fulfilled] : (state,action)=>{
			nutritionAdapter.upsertOne(state.nutrition,action.payload.data.nutrition)
		},

		[removeNutritionMealPhoto.pending] : (state,action)=>{
			const {nutrition_id,id} = action.meta.arg
			const nutrition = state.nutrition.entities[nutrition_id]
			nutrition.food = nutrition.food.filter(
				food => food.type !== 'meal_photo' || food.id !== id
			)
		}

	}
})

export const {
	updatePreview,
	updateFilters,
	setDeleteEvents,
	toggleDeleteItem,
	updateComments,
	clearFilters,
	updateSort,
	setResultsTracking,
	setShowDetails,
	updateCopy,
	updateEditor,
	updateSelectedEventInfo,
	updatePrograms,
	updateExpandedData,
	toggleExpandedData,
	updateManySets,
	addEventForCopying,
	updateCalendarClientSelected,
	updateDefaultClientSelected,
	resetUnseenCommentsCount,
	triggerCalendarRefetch,
	updateNotesEditor,
	filterTrainingSessions
} = slice.actions

export const selectEventToCopy = (state) => {
	const { id, type } = state.allEvents.copy
	return id && state.allEvents[getEventTypeKey(type)].entities[id]
}

export const selectUserEvents = (state,type,user_id_number)=>(
	state.allEvents[getEventTypeKey(type)].ids
	.map(id => state.allEvents[getEventTypeKey(type)].entities[id])
	.filter( event => parseInt(event.client_id) === parseInt(user_id_number) )
)

/**
 * @param state -- state from which to select
 * @param eventType -- event type to select
 * @param display -- all | specific event_id to display
 * @param filter -- client | favorite | all. Type of filtering to perform on all events
 * @param client_id -- if filter == client then this is the client_id for whom to list events
 */
export const selectFilteredEvents = (state, eventType, display, filter, client_id,trainer_id) => {
	//display controls which events to display
	//if it is not all then it means a specific
	//event id is being passed. Only the event
	//with this id should be displayed

	if (display !== 'all') return [{eventType,id:display}]

  const eventTypes = eventType === 'all' ? ['workout','task','nutrition','note'] : [eventType]

	const currentUser = state.users.current_user
	
	const isClient = state.users.current_user.account_type === "client";

	const user_id_number = currentUser.user_id_number

	const { filters, sort } = state.allEvents

	const gym_id = currentUser.gym_id

	const passesFilter = (event) => {
		//check for upcoming
		const upcomingMatch = (new Date(`${event.date}T00:00:00`).getTime() > new Date().getTime())

		//status filter
		const statusMatch = (
			filters.status === 'any' ||
			event.complete == filters.status
		)

		//filter match
		const filterMatch = (
			//filter all lets all non favorite pass through
			(filter === 'all' && parseInt(event.client_id) !== parseInt(user_id_number)) ||
			//if favorte and event is a favorite
			(filter === 'favorite' && parseInt(event.client_id) === parseInt(user_id_number)) ||
			//if client and event belongs to specified client
			(filter === 'client' && parseInt(event.client_id) === parseInt(client_id)) ||
			//if viewing gym events
			(filter === 'gym' && parseInt(event.client_id) === parseInt(gym_id)) ||
			//if viewing other gym trainer's events
			(filter === 'gym_trainer' && parseInt(event.client_id) === parseInt(trainer_id))
		)

		//return match value
		return (
			//filter match 
			filterMatch &&
			//upcoming only match 
			(!filters.upcomingOnly || upcomingMatch) &&
			//status match
			statusMatch
		)
	}

	const filtered = [];

	//map from eventType to entity map of events. Used below in the sorting
	const eventTypeEntitiesMap = {
		workout:[],
		task:[],
		nutrition:[],
		note:[]
	}

	eventTypes.forEach(eventType=>{
		if (eventType!=='note') {
			const filteredIds = state.allEvents[getEventTypeKey(eventType)].ids.filter(id => {
				const event = state.allEvents[getEventTypeKey(eventType)].entities[id]
				return passesFilter(event)
			})
			eventTypeEntitiesMap[eventType] = state.allEvents[getEventTypeKey(eventType)].entities
			const typedIds = filteredIds.map(id=>({eventType,id}))
			filtered.push(...typedIds)
		} else {
			const typedIds = selectNotesIds(state).map(id=>({eventType,id}))
			eventTypeEntitiesMap.note = state.clientNotes.adapter.entities
			filtered.push(...typedIds)
		}
	})

	//now sort
	//NOTE: if filter === favorite the sort will be alphabetical && asc
	const sorted = filtered.sort((a, b) => {
		let aVal, bVal
		const aEvent = eventTypeEntitiesMap[a.eventType][a.id]
		const bEvent = eventTypeEntitiesMap[b.eventType][b.id]
		if(sort.by==='date'&&(filter!=='favorite' || isClient)){
			aVal = new Date(`${aEvent.date}T00:00:00`).getTime()
			bVal = new Date(`${bEvent.date}T00:00:00`).getTime()
		} else {
			aVal = aEvent.note.toLowerCase()
			bVal = bEvent.note.toLowerCase()
		}

		//this is the sort value to use if no query is present
		//if a query is present it will be used as the secondary sort value
		const nonQueryPrimaryVal = (
			sort.desc && (filter !=='favorite' || isClient) ?
			(aVal < bVal ? 1 : -1)
			:
			(aVal > bVal ? 1 : -1)
		)

		if(Boolean(filters.query)){
			const q = filters.query.toLowerCase()
			const delta = slidingQueryComparision(
				aEvent.note.toLowerCase(),
				bEvent.note.toLowerCase(),
				q
			)
			return delta || nonQueryPrimaryVal
		}

		return nonQueryPrimaryVal

	})

	return sorted
}

export default slice.reducer